<template>
  <BaseModal
    v-if="product.bylActionableLink"
    :bem="bemBlock"
    :close-on-backdrop-interaction="true"
    :has-mini-cart="showMinicart"
    :small-close-with-text="showMinicart"
    :is-open="isOpen"
    :modal-modifier="
      modalModifier !== '' ? modalModifier : 'actionable-link-display'
    "
    :show-backdrop="true"
    @closed="$emit('closed')"
  >
    <h2 :class="bemElement('title')">
      {{ title }}
    </h2>
    <div role="document" :class="bemElement('container')">
      <BaseSpinner v-if="loading" />
      <BaseProductGrid
        v-if="isDisplayTypeGrid"
        v-show="products && products.length > 0"
        :analytics="{ location: analyticsConst.location.crossSellingGrid }"
        :class="[
          bemElement('results', {
            grid: isDisplayTypeGrid,
          }),
          'u-scrollbar-byl',
        ]"
        :products="products"
        :show-swatches="false"
        type="crossSelling"
        :infinite-next="true"
        @infinite:next="fetchNextPage"
      />
    </div>
  </BaseModal>
</template>

<script>
import BaseModal from '../BaseModal.vue';
import BaseProductGrid from '../BaseProductGrid.vue';
import BaseSpinner from '../BaseSpinner.vue';
import actionableLinkMixin from '../../mixins/actionableLinkMixin';
import bemClassesMixin from '../../mixins/bemClassesMixin';
import { defaultInfiniteScrollMixin } from '../../mixins/infiniteScrollMixin';
import isMatchedBreakpoint from '../../../common/utils/isMatchedBreakpoint';
import { urlWithSearchParams } from '../../../common/utils/url';
import getAnalyticsEventBus from '../../../common/getAnalyticsEventBus';
import getFrontConfig from '../../../common/getFrontConfig';
import analyticsMixin from '../../mixins/analyticsMixin';

const analyticsEventBus = getAnalyticsEventBus();

const {
  apiConfig,
  responsive,
  urls: { showProductSearchJson },
} = getFrontConfig();

export default {
  name: 'BaseActionableLinkDisplay',

  components: {
    BaseModal,
    BaseProductGrid,
    BaseSpinner,
  },

  mixins: [
    actionableLinkMixin,
    analyticsMixin,
    bemClassesMixin,
    defaultInfiniteScrollMixin,
  ],

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    modalModifier: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    loadedProductSearchUrl: null,
    products: [],
    productSearch: {},
    showMinicart: false,
    loading: true,
  }),

  computed: {
    category() {
      return this.productSearch?.category;
    },
    categoryName() {
      return this.category?.name ?? '';
    },
    isDisplayTypeGrid() {
      return (
        this.actionableLinkDisplayObject.display ===
        this.actionableLinkConst.displayTypes.grid
      );
    },
    showProductSearchJsonUrl() {
      if (this.actionableLinkType === this.actionableLinkConst.types.category) {
        const searchParams = {
          cgid: this.target,
          format: 'json',
          platform: apiConfig.platforms.WEB,
        };
        return urlWithSearchParams(showProductSearchJson, searchParams);
      }
      return null;
    },
    title() {
      if (!this.actionableLinkDisplayObject) {
        return '';
      }

      if (this.actionableLinkType === this.actionableLinkConst.types.category) {
        if (this.actionableLinkDisplayObject.automaticTitle) {
          return this.categoryName;
        }
        return this.actionableLinkDisplayObject.title;
      }

      return '';
    },
    target() {
      return this.actionableLinkDisplayObject?.target;
    },
  },
  watch: {
    async isOpen(newValue) {
      if (newValue === true) {
        this.loading = true;

        if (
          this.actionableLinkType === this.actionableLinkConst.types.category
        ) {
          analyticsEventBus.publish(
            analyticsEventBus.EVENTS.crossSellingGridOpened,
            {
              categoryId: this.target,
            }
          );
        }
      }

      const result = await this.getInitialProducts();
      if (result) {
        this.loading = false;
      }
    },
    products(newProducts, oldProducts) {
      if (newProducts.length > oldProducts.length) {
        this.sendAnalyticsEventViewed();
      }
    },
  },

  async mounted() {
    this.setShowMinicart();
    window.visualViewport.addEventListener('resize', this.setShowMinicart);

    if (this.isOpen) {
      const result = await this.getInitialProducts();
      if (result) {
        this.loading = false;
      }
    }
  },

  beforeDestroy() {
    window.visualViewport.removeEventListener('resize', this.setShowMinicart);
  },

  methods: {
    fetchNextPage($state) {
      const { DIRECTION_BOTTOM } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        async (url) => {
          const searchParams = {
            format: 'json',
            platform: apiConfig.platforms.WEB,
          };
          const nextUrl = urlWithSearchParams(url, searchParams);
          const response = await fetch(nextUrl).then((res) => res.json());
          const productSearch = response?.productSearch;
          if (productSearch) {
            this.productSearch = productSearch;
            this.products = [...this.products, ...productSearch.products];
          }
        },
        DIRECTION_BOTTOM
      );
    },
    async getInitialProducts() {
      if (this.loadedProductSearchUrl === this.showProductSearchJsonUrl) {
        return true;
      }

      const response = await fetch(this.showProductSearchJsonUrl).then((res) =>
        res.json()
      );
      this.productSearch = response || {};
      this.products = response?.products || [];
      this.loadedProductSearchUrl = this.showProductSearchJsonUrl;
      return true;
    },
    sendAnalyticsEventViewed() {
      if (
        this.actionableLinkDisplayObject.display ===
        this.actionableLinkConst.displayTypes.grid
      ) {
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.crossSellingGridViewed,
          {
            category: this.category,
            products: this.products,
          }
        );
      }
    },
    setShowMinicart() {
      const isDesktop = isMatchedBreakpoint.matches(
        `bp_${responsive.bp_1025}`,
        'min-width'
      );
      this.showMinicart = !isDesktop;
    },
  },
};
</script>
